import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { passwordResetConfirm } from "@/lib/api";

export default function ResetPassword() {
  const { tr } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(tr("resetInvalidToken"));
      return;
    }
    if (pwd.length < 8) {
      toast.error(tr("passwordHint"));
      return;
    }
    if (pwd !== pwd2) {
      toast.error(tr("passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await passwordResetConfirm(token, pwd);
      setDone(true);
      toast.success(tr("resetSuccess"));
      setTimeout(() => navigate("/auth"), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || tr("resetInvalidToken"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell hideNav>
      <section
        className="px-5 pt-6 pb-10 space-y-5"
        data-testid="reset-password-screen"
      >
        <h1
          className="text-2xl font-bold text-[rgb(var(--wpe-navy))]"
          data-testid="reset-password-title"
        >
          {tr("resetPasswordTitle")}
        </h1>
        <p className="text-[13px] text-[rgb(var(--wpe-slate))]">
          {tr("resetPasswordSub")}
        </p>

        {!token && (
          <div
            className="wpe-card text-[13px] text-[rgb(var(--wpe-danger))]"
            data-testid="reset-password-missing-token"
          >
            {tr("resetInvalidToken")}
          </div>
        )}

        {token && !done && (
          <form
            onSubmit={onSubmit}
            className="wpe-card space-y-4"
            data-testid="reset-password-form"
          >
            <label className="block">
              <span className="text-[12px] font-semibold text-[rgb(var(--wpe-slate))] block mb-1">
                {tr("newPassword")}
              </span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="wpe-input"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="reset-password-new-input"
              />
              <span className="text-[11px] text-[rgb(var(--wpe-muted))] block mt-1">
                {tr("passwordHint")}
              </span>
            </label>
            <label className="block">
              <span className="text-[12px] font-semibold text-[rgb(var(--wpe-slate))] block mb-1">
                {tr("confirmPassword")}
              </span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                className="wpe-input"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                data-testid="reset-password-confirm-input"
              />
            </label>
            <button
              type="submit"
              className="wpe-cta w-full"
              disabled={submitting}
              data-testid="reset-password-submit-btn"
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <KeyRound size={14} />
              )}
              <span>{tr("resetPasswordTitle")}</span>
            </button>
          </form>
        )}

        {done && (
          <div
            className="wpe-card text-[13px] text-[rgb(var(--wpe-navy))]"
            data-testid="reset-password-success"
          >
            {tr("resetSuccess")}
          </div>
        )}

        <Link
          to="/auth"
          className="wpe-ghost w-full text-[13px] justify-center"
          data-testid="reset-password-back-link"
        >
          {tr("backToSignIn")}
        </Link>

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
